// Greenhold <Certificate> — the hero artifact (Story 3.3).
//
// The canonical Certificate React component: a 4:3 framed-and-engraved
// rendering of an opened share that mirrors `server/src/domain/share.rs::ShareDto`
// 1:1 as the TypeScript type `CertificateShare`. Stories 3.6 (RevealCeremony)
// + 4.x (Vault opened-share grid + share-detail page) + 5.x (marketplace
// listing card) all compose this component as their hero visual.
//
// Story 3.3 — the FIRST hero artifact in packages/components/src/greenhold/:
//   * Renders the 4-tier visual variant (Common cream / Preferred cobalt /
//     Premium royal purple / Founder's burnished gold + shimmer + lifted
//     shadow) — tier-aware visual variant pattern Stories 3.4 / 3.5 / 3.6 /
//     4.x / 5.x will repeat.
//   * Engraved double-border SVG frame (6px outer + 1px inner per the
//     `certificate.border.*` tokens from Story 1.2). vectorEffect=
//     "non-scaling-stroke" keeps stroke weight visually constant across
//     the 4 size variants.
//   * Guilloché trace SVG <pattern> at 0.75px stroke (token
//     `certificate.guilloche.strokeWidth`). v1 simple sinusoidal pattern;
//     real banknote-grade engraving is future polish.
//   * Two render states: `state="building"` (Phase 2 of reveal ceremony,
//     vignette + text hidden, frame draws on via stroke-dashoffset) and
//     `state="complete"` (default, fully rendered, seal parallax when
//     interactive). The transition timing belongs to Story 3.6's
//     RevealCeremony orchestrator.
//   * Per-element `prefers-reduced-motion` gating via
//     `motion-reduce:animate-none` — sets the convention for Story 3.7's
//     reduced-motion alternative reveal work.
//   * Tier badge upper-right via the canonical <TierBadge> from
//     `../greenhold/TierBadge` (Story 3.5 swapped this in; Story 3.3
//     shipped an inline fallback that has since been removed). The
//     Story 3.5 swap closure left CertificateProps + Certificate's
//     public API UNCHANGED — purely an internal substitution.
//   * appreciation-glow overlay variant (Story 5.x marketplace logic
//     drives when to enable; Story 3.3 ships the visual).
//   * Vault grid + share-detail consumers come in Story 4.x.

import { useId, useState, type FC } from "react";
import { cn, isLoadableImageUrl } from "../lib/utils";
import { TierBadge, tierBadgeLabel, type TierBadgeSize } from "./TierBadge";

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type CertificateSize = "thumb" | "md" | "lg" | "hero";
export type CertificateState = "building" | "complete";
export type CertificateTier = "common" | "preferred" | "premium" | "founders";

/**
 * TypeScript mirror of server/src/domain/share.rs::ShareDto. Field names
 * + nullability must stay 1:1 — future Stories 3.6 / 4.x / 5.x consume
 * this type rather than re-declaring it. Drift here = bugs there.
 */
export interface CertificateShare {
  share_id: string;
  dossier_id: string;
  drop_id: string;
  portfolio_id: string;
  portfolio_title: string;
  /** Wax-seal base color of the parent portfolio (migration 0079);
   *  null = default bordeaux. Only the ceremony REPLAY reads it (to
   *  re-render the sealed artifact faithfully); the certificate itself
   *  draws no wax seal. Optional so hand-built fixtures keep compiling. */
  seal_color?: string | null;
  tier: CertificateTier;
  edition_number: number;
  share_serial: number;
  creator_id: string;
  creator_handle: string;
  snapshot_display_name: string;
  snapshot_hello_text: string;
  snapshot_image_url: string;
  snapshot_bio: string | null;
  snapshot_image_signed_url: string | null;
  snapshot_image_signed_url_expires_at: string | null;
  issued_at: string;
  opened_at: string;
  // Epic 8 design pass — display-only certificate axes (server ShareDto
  // condition + signed, migration 0045). `condition` is 0..1 (8 display
  // decimals; HIGHER = more worn) driving <PatronageCertificate> procedural
  // wear + the condition row; `signed` inks the signature scrawl.
  condition: number;
  signed: boolean;
  // Story 4.4 — owner curation state. Mirror of server-side
  // shares.is_highlighted_by_owner / is_favourited_by_owner.
  // ADDITIVE: existing consumers (RevealCeremony, vault thumb,
  // share-detail meta) that don't read these fields continue to work.
  is_highlighted_by_owner: boolean;
  is_favourited_by_owner: boolean;
  // Story 10.2 — owner publish state (shares.is_public_in_collection,
  // migration 0080): whether this certificate is in the owner's public
  // collection. Owner-facing surfaces only; optional so public/anonymous
  // render paths (which never carry it) stay type-compatible.
  is_public_in_collection?: boolean;
}

export interface CertificateProps {
  share: CertificateShare;
  /** Visual size variant. `thumb` for vault grid; `md` for marketplace
   *  listing; `lg` for share-detail page chrome; `hero` for the reveal
   *  ceremony stage + share-detail page hero. */
  size?: CertificateSize;
  /** `building` = Phase-2-of-ceremony staging surface (frame draws on as
   *  stroke-dashoffset trace; vignette + text hidden via opacity-0
   *  placeholders to preserve layout). `complete` = default; full
   *  anatomy renders + seal parallax animates when interactive. */
  state?: CertificateState;
  /** Enables the holographic seal parallax animation in complete state.
   *  When false, the seal renders statically. Off by default for thumbs
   *  (60 FPS budget); on for hero / lg sizes consumers should opt-in. */
  interactive?: boolean;
  /** Shows the post-issuance metadata strip ("EDITION 0042 · DROP MAY
   *  2026") in the footer. Off by default — most consumers don't need
   *  the meta strip. */
  showMeta?: boolean;
  /** Adds the green-glow overlay variant per UX-DR15 ("shows green glow
   *  if share appreciated meaningfully since opening"). Story 5.x
   *  marketplace logic drives this; Story 3.3 ships the visual only. */
  showAppreciationGlow?: boolean;
  /** Optional className for outer container — supports composition. */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier visual variants — color + flag table indexed by share.tier.
// Stories 3.4 / 3.5 / 3.6 / 4.x / 5.x will repeat this `TIER_VARIANTS` Record
// pattern for their tier-aware components.
// ─────────────────────────────────────────────────────────────────────────────

const TIER_VARIANTS: Record<CertificateTier, {
  frameOuterStroke: string;
  frameInnerStroke: string;
  guillocheStroke: string;
  vignetteFallbackBg: string;
  vignetteFallbackFg: string;
  /** Tailwind fill class for the seal SVG circle. Empty string for
   *  founders — that path uses an SVG <linearGradient> instead. */
  sealFill: string;
  /** Tailwind fill class for the seal monogram text. Kept as a literal
   *  here (not interpolated) so Tailwind's purge picks it up. */
  sealMonogramFill: string;
  /** Founder's-only inline gradient stops (linearGradient SVG element).
   *  `null` for the other 3 tiers. */
  sealGradient: { from: string; mid: string; to: string } | null;
  /** Extra container-level classes (Founder's gets the lifted shadow). */
  containerExtras: string;
  /** Only Founder's renders the slow shimmer-gradient sweep overlay. */
  shimmerOverlay: boolean;
}> = {
  common: {
    frameOuterStroke: "stroke-tier-common-fill",
    frameInnerStroke: "stroke-tier-common-accent",
    guillocheStroke: "stroke-tier-common-accent",
    vignetteFallbackBg: "bg-tier-common-fill",
    vignetteFallbackFg: "text-tier-common-fg",
    sealFill: "fill-tier-common-accent",
    sealMonogramFill: "fill-tier-common-fg",
    sealGradient: null,
    containerExtras: "",
    shimmerOverlay: false,
  },
  preferred: {
    frameOuterStroke: "stroke-tier-preferred-fill",
    frameInnerStroke: "stroke-tier-preferred-accent",
    guillocheStroke: "stroke-tier-preferred-accent",
    vignetteFallbackBg: "bg-tier-preferred-fill",
    vignetteFallbackFg: "text-tier-preferred-fg",
    sealFill: "fill-tier-preferred-accent",
    sealMonogramFill: "fill-tier-preferred-fg",
    sealGradient: null,
    containerExtras: "",
    shimmerOverlay: false,
  },
  premium: {
    frameOuterStroke: "stroke-tier-premium-fill",
    frameInnerStroke: "stroke-tier-premium-accent",
    guillocheStroke: "stroke-tier-premium-accent",
    vignetteFallbackBg: "bg-tier-premium-fill",
    vignetteFallbackFg: "text-tier-premium-fg",
    sealFill: "fill-tier-premium-accent",
    sealMonogramFill: "fill-tier-premium-fg",
    sealGradient: null,
    containerExtras: "",
    shimmerOverlay: false,
  },
  founders: {
    frameOuterStroke: "stroke-tier-founders-fill",
    frameInnerStroke: "stroke-tier-founders-accent",
    guillocheStroke: "stroke-tier-founders-accent",
    vignetteFallbackBg: "bg-tier-founders-fill",
    vignetteFallbackFg: "text-tier-founders-fg",
    sealFill: "",
    sealMonogramFill: "fill-tier-founders-fg",
    // Burnished gold → lighter gold accent → burnished gold sweep for
    // the holographic seal. Hex values inlined here mirror the token
    // values at packages/tokens/src/index.ts L82-L91 (Story 1.2 source
    // of truth) — duplication is intentional: SVG linearGradient stops
    // must be hex/rgb, not Tailwind tokens.
    sealGradient: { from: "#c39235", mid: "#e9cf85", to: "#c39235" },
    containerExtras: "shadow-founders-lift",
    shimmerOverlay: true,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Size variants — mirrors the SealedDossier SIZE_CLASSES pattern (Story 2.7).
// ─────────────────────────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<CertificateSize, {
  container: string;
  padding: string;
  vignette: string;
  vignetteInitial: string;
  name: string;
  hello: string;
  edition: string;
  issuer: string;
  seal: string;
  originLink: string;
}> = {
  thumb: {
    container: "w-40",
    padding: "p-3",
    vignette: "w-12 h-12",
    vignetteInitial: "text-xs",
    name: "text-sm",
    hello: "text-xs",
    edition: "text-[10px]",
    issuer: "text-[10px]",
    seal: "w-6 h-6",
    originLink: "text-[10px]",
  },
  md: {
    container: "w-72",
    padding: "p-5",
    vignette: "w-20 h-20",
    vignetteInitial: "text-base",
    name: "text-xl",
    hello: "text-sm",
    edition: "text-xs",
    issuer: "text-xs",
    seal: "w-10 h-10",
    originLink: "text-xs",
  },
  lg: {
    container: "w-96",
    padding: "p-6",
    vignette: "w-24 h-24",
    vignetteInitial: "text-lg",
    name: "text-2xl",
    hello: "text-sm",
    edition: "text-xs",
    issuer: "text-xs",
    seal: "w-12 h-12",
    originLink: "text-xs",
  },
  hero: {
    container: "w-[32rem]",
    padding: "p-8",
    vignette: "w-32 h-32",
    vignetteInitial: "text-2xl",
    name: "text-3xl",
    hello: "text-base",
    edition: "text-sm",
    issuer: "text-sm",
    seal: "w-16 h-16",
    originLink: "text-xs",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Frame SVG geometry — a fixed design canvas in viewBox units. We use
// preserveAspectRatio="none" so the SVG stretches to fill the 4:3 article
// container exactly, and `vector-effect="non-scaling-stroke"` so stroke
// widths stay visually constant at 6px / 1px regardless of size variant.
// ─────────────────────────────────────────────────────────────────────────────

const FRAME_VIEWBOX = "0 0 400 300"; // 4:3 design canvas
const FRAME_OUTER = { x: 12, y: 12, width: 376, height: 276 } as const;
const FRAME_INNER = { x: 19, y: 19, width: 362, height: 262 } as const; // 12 + 6 + 1

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (pure formatting — no side effects).
// ─────────────────────────────────────────────────────────────────────────────

/** Returns the issuer label. Story 3.2 always writes `issuer_kind='self'`
 *  + `issuer_user_id=creator_id`, so the label is the creator's display
 *  name. TODO (post-v1): when org issuance lands, the ShareDto will gain
 *  `issuer_kind` + `issuer_display_name` — switch on that here. */
function formatIssuer(share: CertificateShare): string {
  return share.snapshot_display_name;
}

/** Zero-pads an issue (edition) number to a fixed minimum of 5 digits —
 *  `42` → `"00042"`. Numbers past 99999 keep their natural width (padStart
 *  never truncates), so a 6/7-digit issue renders all its digits.
 *
 *  Exported: this is THE canonical issue-number formatter — every surface
 *  that prints an issue number (cert faces, listing cards, toasts, dialogs)
 *  must use it so the same certificate reads identically everywhere. */
export function formatIssueNumber(n: number): string {
  return String(n).padStart(5, "0");
}

/** Drop label derived from opened_at — e.g., `"MAY 2026"`. Used only
 *  when `showMeta=true`. Locale fixed to 'en' deliberately: drop labels
 *  appear in monospace on the cert; localized month names would visually
 *  drift across renders for the same share.
 *
 *  Exported (Story 3.6) so RevealCeremony's aria-live Phase 3
 *  announcement can use the same exact wording. */
export function formatDropLabel(opened_at: string): string {
  const d = new Date(opened_at);
  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "numeric",
  })
    .format(d)
    .toUpperCase();
}

/** Maps Certificate size variant → TierBadge size variant. Story 3.5
 *  swap mapping: thumb + md → sm (11px badge text); lg → md (14px);
 *  hero → lg (18px). Thumb absorbs a 1-pixel font bump from the prior
 *  inline 10px to TierBadge sm 11px — within visual tolerance. */
function tierBadgeSizeForCertificate(size: CertificateSize): TierBadgeSize {
  switch (size) {
    case "thumb":
    case "md":
      return "sm";
    case "lg":
      return "md";
    case "hero":
      return "lg";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the certificate of an opened share. See module header for the
 * Story 3.3 anatomy + the four tier visual variants.
 *
 * Default state is `complete` — the fully-rendered hero. `building` is
 * the staging surface used by Story 3.6's RevealCeremony orchestrator
 * during Phase 2 of the ceremony beat sequence.
 */
export const Certificate: FC<CertificateProps> = ({
  share,
  size = "md",
  state = "complete",
  interactive = false,
  showMeta = false,
  showAppreciationGlow = false,
  className,
}) => {
  const sizing = SIZE_CLASSES[size];
  const variant = TIER_VARIANTS[share.tier];

  // useId — collision-safe SVG IDs even with many Certificates on one
  // page (the prototype mounts 13+).
  const uid = useId().replace(/:/g, "_");
  const patternId = `gh-cert-guilloche-${uid}`;
  const sealGradientId = `gh-cert-seal-${uid}`;

  // Image fallback: snapshot_image_signed_url can be null (pre-Story 3.2
  // dev/test data) OR can fail to load (SAS expiry, network). Either
  // path → render the initial-circle fallback instead.
  const [imageError, setImageError] = useState(false);
  // Guard against non-loadable URLs (mock-mode `file://` blob reads) so we
  // never set an <img src> the browser will reject; falls back to the
  // initial-circle vignette below. See isLoadableImageUrl.
  const showImage =
    isLoadableImageUrl(share.snapshot_image_signed_url) && !imageError;

  const tierLabel = tierBadgeLabel(share.tier);
  const issuerLabel = formatIssuer(share);
  const editionPadded = formatIssueNumber(share.edition_number);
  // "ISSUE" is the consumer-facing label for edition_number (2026-08 copy
  // rename; the API field + code identifiers keep the edition_ vocabulary).
  const editionText = showMeta
    ? `ISSUE ${editionPadded} · DROP ${formatDropLabel(share.opened_at)}`
    : `ISSUE ${editionPadded}`;
  const ariaLabel = `Share certificate: ${share.snapshot_display_name}, ${tierLabel}, issue ${share.edition_number}`;
  const isBuilding = state === "building";

  // First-letter fallback for the initial circle. Uses character iter
  // (Array.from) so non-ASCII first chars (emoji, combining chars)
  // render as a single grapheme rather than a partial code unit.
  const initialChar =
    Array.from(share.snapshot_display_name.trim())[0]?.toUpperCase() ?? "?";

  // The canonical public drop URL is `/shop/<drop_id>` (single segment) —
  // matches OriginLinkFooter + the Astro public-site route. The historical
  // `/shop/drops/<id>` form was a bug (no such route; the SPA catch-all
  // bounced it to /feed).
  const originHref = `/shop/${share.drop_id}`;

  return (
    // Outer wrapper holds the framed article + the origin-link footer.
    // The link sits OUTSIDE the frame per UX-DR15 — so it can NOT be a
    // child of the overflow-hidden article. Width comes from the size
    // variant; consumers' className composes on top.
    <div className={cn("inline-block", sizing.container, className)}>
      <article
        role="article"
        aria-label={ariaLabel}
        tabIndex={interactive ? 0 : undefined}
        className={cn(
          "relative isolate aspect-[4/3] w-full overflow-hidden rounded-sm",
          "bg-brand-paper-100 text-brand-ink-900",
          "shadow-md",
          variant.containerExtras,
          interactive &&
            "cursor-pointer transition-transform hover:-translate-y-0.5 motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2",
        )}
      >
        {/* ─── Decorative guilloché trace pattern ───
          Renders STATIC in both states. SVG <pattern> approach — a v1
          sinusoidal scribble; real banknote-grade engraving is future
          polish. Pattern id is per-instance (useId) so multiple Certs
          on one page don't collide ref. */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={FRAME_VIEWBOX}
          preserveAspectRatio="none"
        >
          <defs>
            <pattern
              id={patternId}
              width="16"
              height="16"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 8 Q 4 0, 8 8 T 16 8"
                fill="none"
                strokeWidth="0.75"
                vectorEffect="non-scaling-stroke"
                className={cn(variant.guillocheStroke, "opacity-30")}
              />
            </pattern>
          </defs>
          <rect
            x={FRAME_INNER.x}
            y={FRAME_INNER.y}
            width={FRAME_INNER.width}
            height={FRAME_INNER.height}
            fill={`url(#${patternId})`}
          />
        </svg>

        {/* ─── Engraved double-border frame ───
          Two SVG rects with non-scaling stroke so border weight stays
          visually constant across the 4 size variants. In
          `state="building"` both rects play `animate-cert-draw-on`
          (pathLength=100 + stroke-dasharray=100 + offset 100→0).
          Gated by `motion-reduce:animate-none`. */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={FRAME_VIEWBOX}
          preserveAspectRatio="none"
        >
          <rect
            x={FRAME_OUTER.x}
            y={FRAME_OUTER.y}
            width={FRAME_OUTER.width}
            height={FRAME_OUTER.height}
            fill="none"
            strokeWidth={6}
            vectorEffect="non-scaling-stroke"
            pathLength={100}
            className={cn(
              variant.frameOuterStroke,
              isBuilding && "animate-cert-draw-on motion-reduce:animate-none",
            )}
          />
          <rect
            x={FRAME_INNER.x}
            y={FRAME_INNER.y}
            width={FRAME_INNER.width}
            height={FRAME_INNER.height}
            fill="none"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            pathLength={100}
            className={cn(
              variant.frameInnerStroke,
              isBuilding && "animate-cert-draw-on motion-reduce:animate-none",
            )}
          />
        </svg>

        {/* ─── Founder's shimmer overlay ───
          Slow gradient sweep across the frame. Decorative + gated by
          prefers-reduced-motion. Other 3 tiers skip this layer
          entirely. */}
        {variant.shimmerOverlay && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none overflow-hidden rounded-sm"
          >
            <div
              className={cn(
                "absolute inset-y-0 -inset-x-1/2 w-1/2",
                "bg-gradient-to-r from-transparent via-tier-founders-accent/40 to-transparent",
                "animate-cert-founders-shimmer motion-reduce:animate-none",
              )}
            />
          </div>
        )}

        {/* ─── Appreciation glow ───
          Soft green halo inside the frame. Story 5.x marketplace
          decides when to enable; Story 3.3 ships the visual. */}
        {showAppreciationGlow && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none rounded-sm shadow-[inset_0_0_24px_8px_rgba(31,122,77,0.4)]"
          />
        )}

        {/* ─── Inner content area ───
          Padding scales with size; flex column distributes issuer line +
          creator vignette block + footer evenly. In `state="building"`,
          every child element keeps its layout slot but is hidden via
          opacity-0 so the transition to `complete` does not reflow. */}
        <div
          className={cn(
            "relative h-full w-full flex flex-col",
            sizing.padding,
          )}
        >
          {/* Top row: issuer line (left) + tier badge (right). */}
          <div className="flex items-start justify-between gap-2">
            <p
              className={cn(
                "font-mono uppercase tracking-wider opacity-70",
                sizing.issuer,
                isBuilding && "opacity-0",
              )}
            >
              Issued by {issuerLabel}
            </p>
            {/* Tier badge upper-right — Story 3.5 swap (was an inline
                <span> tier-badge fallback in Story 3.3). Per-Certificate-
                size → per-TierBadge-size mapping via
                tierBadgeSizeForCertificate. The opacity-0 in building
                state preserves layout (Phase-2 ceremony staging). */}
            <TierBadge
              tier={share.tier}
              size={tierBadgeSizeForCertificate(size)}
              className={cn(isBuilding && "opacity-0")}
            />
          </div>

          {/* Center: creator vignette + name + hello-text. */}
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center min-h-0">
            {/* Vignette — circular portrait OR initial-circle fallback. */}
            <div
              className={cn(
                "rounded-full overflow-hidden flex items-center justify-center shrink-0",
                sizing.vignette,
                isBuilding && "opacity-0",
              )}
            >
              {showImage ? (
                <img
                  src={share.snapshot_image_signed_url ?? undefined}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div
                  aria-hidden="true"
                  className={cn(
                    "w-full h-full flex items-center justify-center font-heading font-bold",
                    variant.vignetteFallbackBg,
                    variant.vignetteFallbackFg,
                    sizing.vignetteInitial,
                  )}
                >
                  {initialChar}
                </div>
              )}
            </div>

            {/* Creator name. */}
            <h3
              className={cn(
                "font-heading font-bold leading-tight",
                sizing.name,
                isBuilding && "opacity-0",
              )}
            >
              {share.snapshot_display_name}
            </h3>

            {/* Hello-text as semantic <blockquote> — UX-DR15 mandates
                the screen-reader announces this as a quotation (the
                creator's voice). line-clamp-2 enforces "wraps 2 lines
                max + ellipsis". */}
            <blockquote
              className={cn(
                "font-body italic line-clamp-2",
                sizing.hello,
                isBuilding && "opacity-0",
              )}
            >
              {share.snapshot_hello_text}
            </blockquote>
          </div>

          {/* Bottom row: edition info (left) + GH seal (right). */}
          <div className="flex items-end justify-between gap-2">
            <p
              className={cn(
                "font-mono uppercase tracking-wider opacity-70",
                sizing.edition,
                isBuilding && "opacity-0",
              )}
            >
              {editionText}
            </p>
            {/* GH holographic seal — decorative SVG with the monogram. */}
            <svg
              aria-hidden="true"
              viewBox="0 0 40 40"
              className={cn(
                "shrink-0",
                sizing.seal,
                !isBuilding &&
                  interactive &&
                  "animate-cert-seal-parallax motion-reduce:animate-none",
              )}
            >
              {variant.sealGradient && (
                <defs>
                  <linearGradient
                    id={sealGradientId}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={variant.sealGradient.from} />
                    <stop offset="50%" stopColor={variant.sealGradient.mid} />
                    <stop offset="100%" stopColor={variant.sealGradient.to} />
                  </linearGradient>
                </defs>
              )}
              <circle
                cx="20"
                cy="20"
                r="18"
                fill={
                  variant.sealGradient ? `url(#${sealGradientId})` : undefined
                }
                className={cn(variant.sealFill)}
              />
              <text
                x="20"
                y="25"
                textAnchor="middle"
                fontSize="14"
                fontFamily="ui-monospace, monospace"
                fontWeight="700"
                className={cn(variant.sealMonogramFill)}
              >
                GH
              </text>
            </svg>
          </div>
        </div>
      </article>

      {/* ─── Origin-link footer OUTSIDE the frame ───
        Real <a> per UX-DR15 (not a button/onclick). The `↳` is a
        literal U+21B3 character so it's selectable + accessible. */}
      <a
        href={originHref}
        className={cn(
          "block mt-2 text-center font-body",
          "text-brand-forest-500 hover:text-brand-forest-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2 rounded-sm",
          sizing.originLink,
        )}
      >
        ↳ Issued from {share.portfolio_title} · Dossier #{editionPadded}
      </a>
    </div>
  );
};

export default Certificate;
